import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const serif = "'Georgia', 'Times New Roman', serif";

const FaceRecognitionPanel = ({ token, sounds }) => {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [grouping, setGrouping] = useState(false);
  const [groups, setGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [ungroupedCount, setUngroupedCount] = useState(0);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState([]);
  const [merging, setMerging] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/faces/groups`, { headers });
      setGroups(res.data.groups || []);
      setUngroupedCount(res.data.ungrouped_count || 0);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const startScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API}/faces/scan`, {}, { headers });
      setScanResult(res.data);
      if (res.data.faces_detected > 0) fetchGroups();
    } catch (e) { setScanResult({ status: 'error', message: 'Erreur lors du scan.' }); }
    setScanning(false);
  };

  const startGrouping = async () => {
    setGrouping(true);
    try {
      const res = await axios.post(`${API}/faces/group`, {}, { headers });
      if (res.data.groups) fetchGroups();
    } catch (e) { /* ignore */ }
    setGrouping(false);
  };

  const saveName = async (groupId) => {
    if (!nameInput.trim()) return;
    try {
      await axios.post(`${API}/faces/name`, { group_id: groupId, name: nameInput.trim() }, { headers });
      setEditingGroup(null);
      setNameInput('');
      fetchGroups();
    } catch (e) { /* ignore */ }
  };

  const ignoreFace = async (groupId) => {
    try {
      await axios.post(`${API}/faces/ignore`, { group_id: groupId }, { headers });
      setGroups(prev => prev.filter(g => g.group_id !== groupId));
    } catch (e) { /* ignore */ }
  };

  const toggleMergeSelect = (groupId) => {
    setMergeSelection(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const executeMerge = async () => {
    if (mergeSelection.length < 2) return;
    setMerging(true);
    try {
      await axios.post(`${API}/faces/merge`, { group_ids: mergeSelection }, { headers });
      setMergeMode(false);
      setMergeSelection([]);
      fetchGroups();
    } catch (e) { /* ignore */ }
    setMerging(false);
  };

  return (
    <div data-testid="face-recognition-panel" style={{ padding: '20px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontFamily: serif, fontSize: 18, color: '#D4AF37', fontWeight: 300, marginBottom: 6 }}>
          La Reconnaissance
        </p>
        <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', fontWeight: 300 }}>
          Bobine identifie les visages r{'\u00e9'}currents dans vos Tr{'\u00e9'}sors et les relie {'\u00e0'} Mon Cercle.
        </p>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <button
          data-testid="face-scan-btn"
          onClick={startScan}
          disabled={scanning}
          style={{
            fontFamily: serif, fontSize: 13, fontWeight: 300,
            padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
            background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none',
            opacity: scanning ? 0.5 : 1,
          }}
        >
          {scanning ? 'Analyse en cours...' : 'Scanner mes Tr\u00e9sors'}
        </button>
        {ungroupedCount > 0 && (
          <button
            data-testid="face-group-btn"
            onClick={startGrouping}
            disabled={grouping}
            style={{
              fontFamily: serif, fontSize: 13, fontWeight: 300,
              padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
              backgroundColor: 'transparent', color: '#D4AF37',
              border: '1px solid rgba(212,175,55,0.3)',
              opacity: grouping ? 0.5 : 1,
            }}
          >
            {grouping ? 'Regroupement...' : `Regrouper ${ungroupedCount} visage(s)`}
          </button>
        )}
        {groups.length >= 2 && !mergeMode && (
          <button
            data-testid="face-merge-mode-btn"
            onClick={() => { setMergeMode(true); setMergeSelection([]); }}
            style={{
              fontFamily: serif, fontSize: 13, fontWeight: 300,
              padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
              backgroundColor: 'transparent', color: '#D4AF37',
              border: '1px solid rgba(212,175,55,0.3)',
            }}
          >
            Fusionner des identit&eacute;s
          </button>
        )}
      </div>

      {/* Merge mode banner */}
      {mergeMode && (
        <div data-testid="merge-banner" style={{
          marginBottom: 20, padding: '14px 16px', borderRadius: 10,
          background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', fontWeight: 300 }}>
            S&eacute;lectionnez les visages qui sont la m&ecirc;me personne, puis confirmez la fusion.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              data-testid="merge-confirm-btn"
              onClick={executeMerge}
              disabled={mergeSelection.length < 2 || merging}
              style={{
                fontFamily: serif, fontSize: 12, fontWeight: 300,
                padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
                backgroundColor: mergeSelection.length >= 2 ? '#D4AF37' : 'rgba(212,175,55,0.15)',
                color: mergeSelection.length >= 2 ? '#000' : '#D4AF37',
                border: 'none', transition: 'all 0.2s',
              }}
            >
              {merging ? 'Fusion...' : `Fusionner ${mergeSelection.length} identit\u00e9(s)`}
            </button>
            <button
              data-testid="merge-cancel-btn"
              onClick={() => { setMergeMode(false); setMergeSelection([]); }}
              style={{
                fontFamily: serif, fontSize: 12, fontWeight: 300,
                padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
                backgroundColor: 'transparent', color: '#D4AF37',
                border: '1px solid rgba(212,175,55,0.15)',
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Scan result */}
      {scanResult && (
        <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 8, background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.1)' }}>
          <p style={{ fontFamily: serif, fontSize: 12, color: '#D4AF37' }}>
            {scanResult.status === 'success'
              ? `${scanResult.photos_scanned} photo(s) analys\u00e9e(s), ${scanResult.faces_detected} visage(s) d\u00e9tect\u00e9(s)`
              : scanResult.message}
          </p>
        </div>
      )}

      {/* Face groups */}
      {groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {groups.map((g, i) => {
            const isSelected = mergeSelection.includes(g.group_id);
            return (
            <div
              key={g.group_id}
              data-testid={`face-group-${i}`}
              onClick={mergeMode ? () => toggleMergeSelect(g.group_id) : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '14px 16px', borderRadius: 10,
                border: isSelected ? '2px solid #D4AF37' : '1px solid rgba(212,175,55,0.12)',
                background: isSelected ? 'rgba(212,175,55,0.08)' : 'rgba(212,175,55,0.02)',
                cursor: mergeMode ? 'pointer' : 'default',
                transition: 'all 0.2s',
              }}
            >
              {/* Face thumbnail */}
              {g.sample_crop && (
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', overflow: 'hidden',
                  border: '2px solid rgba(212,175,55,0.3)', flexShrink: 0,
                }}>
                  <img
                    src={`data:image/jpeg;base64,${g.sample_crop}`}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {editingGroup === g.group_id ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      data-testid={`face-name-input-${i}`}
                      value={nameInput}
                      onChange={e => setNameInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && saveName(g.group_id)}
                      placeholder="Nom de cette personne"
                      autoFocus
                      style={{
                        fontFamily: serif, fontSize: 13, color: '#D4AF37',
                        background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)',
                        borderRadius: 6, padding: '6px 10px', flex: 1, outline: 'none',
                      }}
                    />
                    <button
                      onClick={() => saveName(g.group_id)}
                      style={{
                        fontFamily: serif, fontSize: 11, padding: '6px 12px',
                        borderRadius: 6, cursor: 'pointer',
                        background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none',
                      }}
                    >
                      OK
                    </button>
                  </div>
                ) : (
                  <>
                    <p style={{ fontFamily: serif, fontSize: 14, color: g.name ? '#D4AF37' : '#FAF3E0', fontWeight: 300, marginBottom: 2 }}>
                      {g.name || 'Visage non nomm\u00e9'}
                    </p>
                    <p style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0' }}>
                      {g.photo_count} Tr{'\u00e9'}sor{g.photo_count > 1 ? 's' : ''}
                    </p>
                  </>
                )}
              </div>
              {/* Name button + Ignore button — hidden in merge mode */}
              {!mergeMode && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                {editingGroup !== g.group_id && (
                  <button
                    data-testid={`face-name-btn-${i}`}
                    onClick={() => { setEditingGroup(g.group_id); setNameInput(g.name || ''); }}
                    style={{
                      fontFamily: serif, fontSize: 11, color: '#D4AF37',
                      background: 'none', border: '1px solid rgba(212,175,55,0.15)',
                      borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {g.name ? 'Renommer' : 'Nommer'}
                  </button>
                )}
                {!g.name && editingGroup !== g.group_id && (
                  <button
                    data-testid={`face-ignore-btn-${i}`}
                    onClick={() => ignoreFace(g.group_id)}
                    title="Ignorer ce visage"
                    style={{
                      width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
                      background: 'none', border: '1px solid rgba(212,175,55,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#D4AF37', fontSize: 14, fontWeight: 300,
                      transition: 'all 0.2s',
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              )}
              {/* Merge mode checkbox indicator */}
              {mergeMode && (
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  border: isSelected ? '2px solid #D4AF37' : '2px solid rgba(212,175,55,0.2)',
                  backgroundColor: isSelected ? '#D4AF37' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                  {isSelected && <span style={{ color: '#0a0a0a', fontSize: 14, fontWeight: 700 }}>&#10003;</span>}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {groups.length === 0 && !scanning && (
        <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', textAlign: 'center', marginTop: 20 }}>
          Aucun visage d{'\u00e9'}tect{'\u00e9'}. Lancez un scan pour commencer.
        </p>
      )}
    </div>
  );
};

export default FaceRecognitionPanel;
